/**
 * Settings, cues, and the persistence envelope.
 *
 * Three things live together here because they are one contract in practice:
 * the user's cue preferences decide which output channels exist, the cue
 * schedule projection turns those preferences into a deterministic timeline,
 * and the persistence envelope is what makes a preference survive a reload
 * without the app ever guessing at bytes it could not read.
 *
 * Nothing in this file performs I/O, touches React, or reads a browser global.
 * Adapters in @ff/store and @ff/engine supply the effects; the rules are here
 * so they can be tested without a runtime.
 */

import type { Milliseconds } from "./time";

/**
 * Nominal typing without a runtime cost. Kept unexported so sibling contexts
 * can declare their own brand helper without colliding at the package barrel.
 */
declare const brand: unique symbol;
type Brand<T, K extends string> = T & { readonly [brand]: K };

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/** The persisted settings record. One per browser profile. */
export type SettingsProfileId = Brand<string, "SettingsProfileId">;

/**
 * A projected cue event. The id participates in collision resolution, so it
 * must be stable across two projections of the same timeline — otherwise a
 * re-projection after a pause could silently reorder simultaneous cues.
 */
export type CueEventId = Brand<string, "CueEventId">;

/** Names one persistent store. Used as the key of the fail-closed registry. */
export type StoreId = Brand<string, "StoreId">;

/**
 * Branded so a schema version can never be passed where a record count or an
 * array index is expected; the two are both small integers and mixing them
 * would defeat the future-schema refusal.
 */
export type SchemaVersion = Brand<number, "SchemaVersion">;

export function settingsProfileId(raw: string): SettingsProfileId {
  return raw as SettingsProfileId;
}

export function cueEventId(raw: string): CueEventId {
  return raw as CueEventId;
}

export function storeId(raw: string): StoreId {
  return raw as StoreId;
}

export function schemaVersion(raw: number): SchemaVersion {
  return raw as SchemaVersion;
}

// ---------------------------------------------------------------------------
// Device noun
// ---------------------------------------------------------------------------

/**
 * The native app interpolates "iPhone"/"iPad" into 31 user-facing strings by
 * device idiom while branching every layout on width. The web build resolves
 * that inconsistency by never naming a device: one neutral noun everywhere.
 */
export const DEVICE_NOUN = "this device" as const;

// ---------------------------------------------------------------------------
// Appearance
// ---------------------------------------------------------------------------

export type ColorScheme = "light" | "dark";

export type AppearanceMode = "system" | "day" | "night";

/** Picker order is fixed; the segmented control renders these left to right. */
export const APPEARANCE_MODES: readonly AppearanceMode[] = [
  "system",
  "day",
  "night",
];

export interface AppearanceModeDescriptor {
  readonly mode: AppearanceMode;
  readonly title: string;
  readonly detail: string;
  readonly symbol: string;
  /** null means "defer to the system"; only System is unresolved. */
  readonly scheme: ColorScheme | null;
}

export const APPEARANCE_MODE_DESCRIPTORS: readonly AppearanceModeDescriptor[] =
  [
    {
      mode: "system",
      title: "System",
      detail: "Match this device automatically",
      symbol: "circle.lefthalf.filled",
      scheme: null,
    },
    {
      mode: "day",
      title: "Day",
      detail: "Warm Mesh White surfaces with deep Infrared",
      symbol: "sun.max.fill",
      scheme: "light",
    },
    {
      mode: "night",
      title: "Night",
      detail: "Carbon surfaces with bright Corner Infrared",
      symbol: "moon.stars.fill",
      scheme: "dark",
    },
  ];

export const DEFAULT_APPEARANCE_MODE: AppearanceMode = "system";

/**
 * Any unrecognized stored value resolves to System rather than failing. An
 * unreadable appearance preference must never keep the app off-screen, and
 * System is the only choice that is correct for every device.
 */
export function resolveAppearanceMode(
  stored: string | undefined,
): AppearanceMode {
  for (const mode of APPEARANCE_MODES) {
    if (mode === stored) return mode;
  }
  return DEFAULT_APPEARANCE_MODE;
}

export function resolveColorScheme(
  mode: AppearanceMode,
  systemScheme: ColorScheme,
): ColorScheme {
  if (mode === "day") return "light";
  if (mode === "night") return "dark";
  return systemScheme;
}

const SCHEME_WORD: Readonly<Record<ColorScheme, string>> = {
  light: "Day",
  dark: "Night",
};

/**
 * The status line exists so the picker never leaves the user guessing which
 * appearance is actually in force — following System is not a visible state
 * on its own.
 */
export function appearanceStatusLine(
  mode: AppearanceMode,
  systemScheme: ColorScheme,
): string {
  if (mode === "system") {
    return `Following System. This device is currently using ${SCHEME_WORD[systemScheme]}.`;
  }
  const word = mode === "day" ? "Day" : "Night";
  return `Fighting Form will stay in ${word}, independent of the system appearance.`;
}

export const APPEARANCE_SCREEN_COPY = {
  eyebrow: "Infrared Corner",
  title: "Choose your corner light",
  body: "Day and Night share the same hierarchy: neutral surfaces, clear text, and Corner Infrared only where attention matters now.",
} as const;

export type AppearanceRuleTone = "infrared" | "warning" | "neutral";

export interface AppearanceRuleCard {
  readonly title: string;
  readonly body: string;
  readonly tone: AppearanceRuleTone;
}

export const APPEARANCE_RULE_CARDS: readonly AppearanceRuleCard[] = [
  {
    title: "Corner Infrared",
    body: "Selection, focus, live work, progress, and earned completion.",
    tone: "infrared",
  },
  {
    title: "Risk stays separate",
    body: "Warnings use Safety Amber; destructive actions use Critical Red plus explicit words and confirmation.",
    tone: "warning",
  },
  {
    title: "Meaning survives color",
    body: "State labels, symbols, and shapes remain present in both appearances and accessibility modes.",
    tone: "neutral",
  },
];

/**
 * Both preview cards render hard-coded Day and Night palettes regardless of
 * the active scheme: the point of the preview is to show the choice, not the
 * current state.
 */
export function appearancePreviewAccessibility(
  scheme: ColorScheme,
  isSelected: boolean,
): { readonly label: string; readonly value: string } {
  return {
    label: `${SCHEME_WORD[scheme]} appearance preview`,
    value: isSelected ? "Selected" : "Not selected",
  };
}

// ---------------------------------------------------------------------------
// Accessibility overrides
// ---------------------------------------------------------------------------

/**
 * `dynamicTypeSize.isAccessibilitySize` is a single boolean on Apple platforms
 * and it collapses every multi-pane composition in the app. The web has no
 * equivalent signal, and `rem` scaling cannot stand in because the branch
 * changes structure rather than size — so it becomes an explicit setting.
 * Differentiate Without Color and Button Shapes have no media query at all;
 * without them, unselected selection rings never render.
 */
export type TextSizePreference = "system" | "accessibility";

export type SystemOverridablePreference = "system" | "on";

export interface AccessibilityOverrides {
  readonly textSize: TextSizePreference;
  readonly increaseContrast: SystemOverridablePreference;
  readonly differentiateWithoutColor: boolean;
  readonly buttonShapes: boolean;
  /** Native nils animations out entirely rather than shortening them. */
  readonly reduceMotion: SystemOverridablePreference;
}

export const DEFAULT_ACCESSIBILITY_OVERRIDES: AccessibilityOverrides = {
  textSize: "system",
  increaseContrast: "system",
  differentiateWithoutColor: false,
  buttonShapes: false,
  reduceMotion: "system",
};

// ---------------------------------------------------------------------------
// Cue settings
// ---------------------------------------------------------------------------

/**
 * Storage keys are part of the contract: they are what a returning user's
 * browser profile already contains, and renaming one silently resets a
 * preference to its default.
 */
export type CueToggleKey =
  | "cues.bells"
  | "cues.voice"
  | "cues.haptics"
  | "cues.duckMusic"
  | "cues.halfway"
  | "cues.finalTen"
  | "cues.drillChange"
  | "cues.comboCalls"
  | "cues.lockScreen";

export const VOICE_STYLE_KEY = "cues.voiceStyle" as const;

/**
 * Absent key means true for every output and milestone toggle. That convention
 * is what makes "never set" distinguishable from "explicitly turned off", so a
 * cleared store restores a fully cued workout rather than a silent one.
 * Lock-screen cues are the single exception: they require a permission the user
 * has not granted yet, so their absence means off.
 */
export const CUE_TOGGLE_DEFAULTS: Readonly<Record<CueToggleKey, boolean>> = {
  "cues.bells": true,
  "cues.voice": true,
  "cues.haptics": true,
  "cues.duckMusic": true,
  "cues.halfway": true,
  "cues.finalTen": true,
  "cues.drillChange": true,
  "cues.comboCalls": true,
  "cues.lockScreen": false,
};

export type StoredCueFlags = Partial<Record<CueToggleKey, boolean>>;

export interface CueSettings {
  readonly bells: boolean;
  readonly voice: boolean;
  readonly haptics: boolean;
  readonly ducksMusic: boolean;
  readonly halfway: boolean;
  readonly finalTen: boolean;
  readonly drillChange: boolean;
  readonly comboCalls: boolean;
  readonly lockScreenCues: boolean;
  readonly voiceStyle: VoiceStyle;
}

export const DEFAULT_CUE_SETTINGS: CueSettings = {
  bells: true,
  voice: true,
  haptics: true,
  ducksMusic: true,
  halfway: true,
  finalTen: true,
  drillChange: true,
  comboCalls: true,
  lockScreenCues: false,
  voiceStyle: "Focused",
};

export function resolveCueSettings(
  stored: StoredCueFlags,
  storedVoiceStyle: string | undefined,
): CueSettings {
  return {
    bells: stored["cues.bells"] ?? CUE_TOGGLE_DEFAULTS["cues.bells"],
    voice: stored["cues.voice"] ?? CUE_TOGGLE_DEFAULTS["cues.voice"],
    haptics: stored["cues.haptics"] ?? CUE_TOGGLE_DEFAULTS["cues.haptics"],
    ducksMusic:
      stored["cues.duckMusic"] ?? CUE_TOGGLE_DEFAULTS["cues.duckMusic"],
    halfway: stored["cues.halfway"] ?? CUE_TOGGLE_DEFAULTS["cues.halfway"],
    finalTen: stored["cues.finalTen"] ?? CUE_TOGGLE_DEFAULTS["cues.finalTen"],
    drillChange:
      stored["cues.drillChange"] ?? CUE_TOGGLE_DEFAULTS["cues.drillChange"],
    comboCalls:
      stored["cues.comboCalls"] ?? CUE_TOGGLE_DEFAULTS["cues.comboCalls"],
    lockScreenCues:
      stored["cues.lockScreen"] ?? CUE_TOGGLE_DEFAULTS["cues.lockScreen"],
    voiceStyle: resolveVoiceStyle(storedVoiceStyle),
  };
}

// ---------------------------------------------------------------------------
// Output channels
// ---------------------------------------------------------------------------

/**
 * The native taxonomy. Haptics is retained as a case because the cue engine's
 * emission contract still distinguishes it, but see WEB_CUE_OUTPUT_CHANNELS:
 * `navigator.vibrate` does not exist in Safari or on iOS at all, so haptics
 * can never be an enabled output on the web build.
 */
export type CueOutputChannel = "bells" | "voice" | "haptics";

/**
 * The channels the "{n} of N outputs" math counts. Haptics is excluded rather
 * than reported as off, because a count that can never reach its maximum reads
 * as a broken app rather than an unavailable platform feature.
 */
export const WEB_CUE_OUTPUT_CHANNELS: readonly CueOutputChannel[] = [
  "bells",
  "voice",
];

/**
 * §2.8 requires every critical transition to survive any single channel being
 * disabled. Haptics is that missing channel on the web, so a synchronized
 * full-bleed tint pulse carries the redundancy instead — it is always present
 * and is never counted as a user-toggleable output.
 */
export const CUE_VISUAL_PULSE_IS_ALWAYS_ON = true as const;

const CUE_OUTPUT_SPOKEN_NAME: Readonly<Record<CueOutputChannel, string>> = {
  bells: "bells",
  voice: "voice",
  haptics: "haptics",
};

export function enabledCueOutputs(
  settings: CueSettings,
): readonly CueOutputChannel[] {
  return WEB_CUE_OUTPUT_CHANNELS.filter((channel) =>
    channel === "bells" ? settings.bells : settings.voice,
  );
}

export function disabledCueOutputs(
  settings: CueSettings,
): readonly CueOutputChannel[] {
  return WEB_CUE_OUTPUT_CHANNELS.filter((channel) =>
    channel === "bells" ? !settings.bells : !settings.voice,
  );
}

/**
 * Oxford-comma joining, scoped to cue-output names so a future localization
 * pass has one list builder to extract rather than one embedded in control
 * flow.
 */
export function formatCueOutputList(items: readonly string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0] ?? ""} and ${items[1] ?? ""}`;
  const head = items.slice(0, -1).join(", ");
  return `${head}, and ${items[items.length - 1] ?? ""}`;
}

function sentenceCase(value: string): string {
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** "{n} of 2 outputs" — the pill and the sidebar button share this label. */
export function cueOutputCompactLabel(settings: CueSettings): string {
  const enabled = enabledCueOutputs(settings).length;
  return `${enabled} of ${WEB_CUE_OUTPUT_CHANNELS.length} outputs`;
}

export const CUE_SUMMARY_VISUAL_ONLY = "Visual cues only" as const;
export const CUE_PILL_VISUAL_ONLY = "Visual only" as const;

export function cueOutputSummary(settings: CueSettings): string {
  if (enabledCueOutputs(settings).length === 0) return CUE_SUMMARY_VISUAL_ONLY;
  return `${cueOutputCompactLabel(settings)} enabled`;
}

export function cuePillLabel(settings: CueSettings): string {
  if (enabledCueOutputs(settings).length === 0) return CUE_PILL_VISUAL_ONLY;
  return cueOutputCompactLabel(settings);
}

export type CueStatusTone = "volt" | "sky" | "fog";

export function cueStatusTone(settings: CueSettings): CueStatusTone {
  const enabled = enabledCueOutputs(settings).length;
  if (enabled === 0) return "fog";
  if (enabled === WEB_CUE_OUTPUT_CHANNELS.length) return "volt";
  return "sky";
}

/**
 * Composed separately from the visible label because the spoken form has to
 * enumerate exactly which outputs are OFF. Given the cue-redundancy
 * commitment, that sentence is the only thing telling a non-sighted user they
 * are about to train with no audible cue at all.
 */
export function cueStatusAccessibilityLabel(settings: CueSettings): string {
  const enabled = enabledCueOutputs(settings);
  const disabled = disabledCueOutputs(settings);
  const names = (channels: readonly CueOutputChannel[]): string[] =>
    channels.map((channel) => CUE_OUTPUT_SPOKEN_NAME[channel]);

  if (enabled.length === 0) {
    const list = sentenceCase(formatCueOutputList(names(disabled)));
    return `Cue outputs: visual only. ${list} ${
      disabled.length === 1 ? "is" : "are"
    } off.`;
  }

  const enabledSentence = `Cue outputs: ${formatCueOutputList(
    names(enabled),
  )} enabled.`;
  if (disabled.length === 0) return enabledSentence;

  const disabledSentence = `${sentenceCase(
    formatCueOutputList(names(disabled)),
  )} ${disabled.length === 1 ? "is" : "are"} off.`;
  return `${enabledSentence} ${disabledSentence}`;
}

// ---------------------------------------------------------------------------
// Voice style
// ---------------------------------------------------------------------------

/**
 * The stored value is the display label itself. Keeping the literal casing
 * means an existing profile keeps its choice without a migration.
 */
export type VoiceStyle = "Calm" | "Focused" | "Energetic";

export const VOICE_STYLES: readonly VoiceStyle[] = [
  "Calm",
  "Focused",
  "Energetic",
];

export const DEFAULT_VOICE_STYLE: VoiceStyle = "Focused";

export interface VoiceStyleDescriptor {
  readonly style: VoiceStyle;
  /** AVSpeechUtterance rate, 0…1 with 0.5 default. Parity reference only. */
  readonly nativeRate: number;
  readonly nativePitch: number;
  /** Web Speech rate, 0.1…10 with 1.0 default — a different scale entirely. */
  readonly webRate: number;
  readonly webPitch: number;
}

export const VOICE_STYLE_DESCRIPTORS: readonly VoiceStyleDescriptor[] = [
  {
    style: "Calm",
    nativeRate: 0.44,
    nativePitch: 0.88,
    webRate: 0.9,
    webPitch: 0.88,
  },
  {
    style: "Focused",
    nativeRate: 0.48,
    nativePitch: 0.92,
    webRate: 1.0,
    webPitch: 0.92,
  },
  {
    style: "Energetic",
    nativeRate: 0.52,
    nativePitch: 1.0,
    webRate: 1.1,
    webPitch: 1.0,
  },
];

/** Any unrecognized stored string falls through to the Focused values. */
export function resolveVoiceStyle(stored: string | undefined): VoiceStyle {
  for (const style of VOICE_STYLES) {
    if (style === stored) return style;
  }
  return DEFAULT_VOICE_STYLE;
}

export function voiceStyleDescriptor(style: VoiceStyle): VoiceStyleDescriptor {
  for (const descriptor of VOICE_STYLE_DESCRIPTORS) {
    if (descriptor.style === style) return descriptor;
  }
  // Unreachable for a well-typed VoiceStyle; Focused is the documented anchor.
  return {
    style: "Focused",
    nativeRate: 0.48,
    nativePitch: 0.92,
    webRate: 1.0,
    webPitch: 0.92,
  };
}

export const SPEECH_LANGUAGE = "en-US" as const;

/**
 * What the cue engine actually holds. Rate and pitch are already in the Web
 * Speech scale so no adapter has to remember which platform's numbers these
 * are.
 */
export interface CuePreferences {
  readonly bells: boolean;
  readonly voice: boolean;
  readonly haptics: boolean;
  readonly ducksMusic: boolean;
  readonly voiceRate: number;
  readonly voicePitch: number;
}

export function cuePreferences(settings: CueSettings): CuePreferences {
  const descriptor = voiceStyleDescriptor(settings.voiceStyle);
  return {
    bells: settings.bells,
    voice: settings.voice,
    // Haptics can never be enabled on the web; the visual pulse substitutes.
    haptics: false,
    ducksMusic: settings.ducksMusic,
    voiceRate: descriptor.webRate,
    voicePitch: descriptor.webPitch,
  };
}

export const DEFAULT_CUE_PREFERENCES: CuePreferences = {
  bells: true,
  voice: true,
  haptics: false,
  ducksMusic: true,
  voiceRate: 1.0,
  voicePitch: 0.92,
};

/**
 * The milestone toggles are read once, when the engine is constructed, and
 * frozen for the session. Changing one mid-workout deliberately has no effect:
 * a cue timeline that mutates under a running round produces cues the user did
 * not hear the start of.
 */
export interface FrozenMilestoneCues {
  readonly halfway: boolean;
  readonly finalTen: boolean;
  readonly drillChange: boolean;
  readonly comboCalls: boolean;
}

export function freezeMilestoneCues(
  settings: CueSettings,
): FrozenMilestoneCues {
  return {
    halfway: settings.halfway,
    finalTen: settings.finalTen,
    drillChange: settings.drillChange,
    comboCalls: settings.comboCalls,
  };
}

// ---------------------------------------------------------------------------
// Audio: bell and speech descriptors
// ---------------------------------------------------------------------------

export interface BellPartial {
  readonly frequencyHz: number;
  readonly gain: number;
}

/**
 * The bell is fully original — there is no licensed sample anywhere in the
 * product. It must be re-synthesized from this formula, never approximated
 * with a substitute tone, or the app ships an unlicensed asset by accident.
 */
export const BELL_PARTIALS: readonly BellPartial[] = [
  { frequencyHz: 860, gain: 0.48 },
  { frequencyHz: 1245, gain: 0.3 },
  { frequencyHz: 1735, gain: 0.16 },
];

export const BELL_SAMPLE_RATE = 44_100 as const;
export const BELL_DURATION_SECONDS = 0.72 as const;
export const BELL_FRAME_COUNT = 31_680 as const;
export const BELL_ENVELOPE_DECAY = 5.2 as const;
export const BELL_ATTACK_SLOPE = 85 as const;

/** The bundled notification variant uses identical math over a longer tail. */
export const NOTIFICATION_BELL_DURATION_SECONDS = 0.85 as const;
export const NOTIFICATION_BELL_FRAME_COUNT = 37_485 as const;

/**
 * One frame of the procedural bell at time `t` seconds. The attack ramp
 * reaches full amplitude at roughly 11.8 ms, which is what keeps the strike
 * from clicking at the buffer start.
 */
export function bellSample(t: number): number {
  const envelope = Math.exp(-BELL_ENVELOPE_DECAY * t);
  const strike = Math.min(1, t * BELL_ATTACK_SLOPE);
  let sum = 0;
  for (const partial of BELL_PARTIALS) {
    sum += Math.sin(2 * Math.PI * partial.frequencyHz * t) * partial.gain;
  }
  return sum * envelope * strike;
}

export type BellStrikeCount = 1 | 2;

/** A double bell is two strikes of the same buffer, not a different sound. */
export const BELL_SECOND_STRIKE_DELAY: Milliseconds = 380;
export const BELL_TEARDOWN_SINGLE: Milliseconds = 850;
export const BELL_TEARDOWN_DOUBLE: Milliseconds = 1_250;

export function bellTeardownDelay(strikes: BellStrikeCount): Milliseconds {
  return strikes > 1 ? BELL_TEARDOWN_DOUBLE : BELL_TEARDOWN_SINGLE;
}

export type CueAnnouncementPriority = "priority" | "standard";

export type HapticProfile =
  | "notificationSuccess"
  | "notificationWarning"
  | "impactHeavy"
  | "impactMedium";

/**
 * The haptic fires regardless of the bells preference on native. On the web
 * this profile selects the visual pulse instead, which is why it stays part of
 * the descriptor rather than being resolved away at the call site.
 */
export function hapticProfileForBell(strikes: BellStrikeCount): HapticProfile {
  return strikes > 1 ? "notificationSuccess" : "notificationWarning";
}

export function hapticProfileForAnnouncement(
  priority: CueAnnouncementPriority,
): HapticProfile {
  return priority === "priority" ? "impactHeavy" : "impactMedium";
}

export interface CueAnnouncement {
  readonly text: string;
  /** A priority announcement cancels whatever is currently being spoken. */
  readonly priority: CueAnnouncementPriority;
}

/**
 * What an adapter is asked to emit for one cue. A discriminated union rather
 * than a struct of optionals so an emission can never be "a bell and also
 * speech and also neither".
 */
export type CueEmission =
  | { readonly channel: "bell"; readonly strikes: BellStrikeCount }
  | { readonly channel: "speech"; readonly announcement: CueAnnouncement }
  | { readonly channel: "visualPulse"; readonly profile: HapticProfile };

/**
 * The `AudioContext` cannot produce sound until a user gesture unlocks it —
 * a step the native app never needed. Modeling the lock explicitly keeps the
 * failure visible instead of shipping a first round with no bell.
 */
export type AudioUnlockState = "locked" | "unlocking" | "unlocked" | "blocked";

export const AUDIO_UNLOCK_COPY = {
  prompt: "Turn on sound",
  detail: "Browsers need one tap before Fighting Form can play a bell.",
  blocked: "Sound is unavailable in this browser. Cues stay visible onscreen.",
} as const;

// ---------------------------------------------------------------------------
// Cue kinds, priority, and schedule projection
// ---------------------------------------------------------------------------

export type CueKind =
  | "segmentTransition"
  | "halfway"
  | "finalCountdown"
  | "drillSwitch"
  | "burstWork"
  | "burstReset"
  | "workoutComplete";

/**
 * Only one cue may occupy a given millisecond instant. Higher wins. The order
 * is deliberate: a completion cue must never be displaced by a cadence cue
 * that happens to land on the same instant.
 */
export const CUE_KIND_PRIORITY: Readonly<Record<CueKind, number>> = {
  workoutComplete: 70,
  segmentTransition: 60,
  finalCountdown: 50,
  halfway: 40,
  drillSwitch: 30,
  burstReset: 20,
  burstWork: 10,
};

/** Roles the projection needs; the plan's own segment taxonomy maps onto it. */
export type CueSegmentRole = "preparation" | "work" | "recovery";

/**
 * A standalone drill interval schedules no drill-switch, combo, or burst cues
 * — a rep drill interrupted by boxing-specific calls is worse than silence.
 */
export type CueSegmentGuidance = "guidedRound" | "standaloneDrill";

export interface CueBurstCadence {
  readonly workDuration: Milliseconds;
  readonly resetDuration: Milliseconds;
}

/**
 * The flattened projection input. Deliberately carries no plan name, segment
 * title, or segment detail: §2.9 forbids user-authored text in a scheduled
 * cue, and the cheapest way to enforce that is to never hand the projector any.
 */
export interface CueScheduleSegment {
  readonly index: number;
  readonly role: CueSegmentRole;
  readonly guidance: CueSegmentGuidance;
  /** Offset from the projection origin, not a wall-clock instant. */
  readonly startsAt: Milliseconds;
  readonly duration: Milliseconds;
  /** Round ordinal for the generic transition body, when the segment has one. */
  readonly roundNumber: number | null;
  readonly drillSwitchInterval: Milliseconds | null;
  readonly burst: CueBurstCadence | null;
  /** Raw enum value of the following segment kind; the transition title. */
  readonly nextSegmentKindRawValue: string | null;
}

export interface CueSchedulePolicy {
  readonly maximumEvents: number;
  readonly minimumLeadTime: Milliseconds;
  readonly includesBurstCues: boolean;
}

export const DEFAULT_CUE_SCHEDULE_POLICY: CueSchedulePolicy = {
  maximumEvents: 56,
  minimumLeadTime: 1_000,
  includesBurstCues: true,
};

/** The system will hold 64 pending notifications; 4 stay reserved. */
export const CUE_SCHEDULE_SYSTEM_BUDGET = 64 as const;
export const CUE_SCHEDULE_RESERVED_SLOTS = 4 as const;
export const CUE_SCHEDULE_HARD_CAP = 56 as const;
export const FINAL_COUNTDOWN_SECONDS = 10 as const;

export function cueScheduleCapacity(
  policy: CueSchedulePolicy,
  unrelatedPendingCount: number,
): number {
  const budget =
    CUE_SCHEDULE_SYSTEM_BUDGET -
    CUE_SCHEDULE_RESERVED_SLOTS -
    Math.max(0, unrelatedPendingCount);
  const cap = Math.min(CUE_SCHEDULE_HARD_CAP, policy.maximumEvents, budget);
  return Math.max(0, cap);
}

export interface CueScheduleRequest {
  /** Elapsed time already consumed; cues before it are not projected. */
  readonly elapsed: Milliseconds;
  readonly segments: readonly CueScheduleSegment[];
  readonly milestones: FrozenMilestoneCues;
  readonly policy: CueSchedulePolicy;
  readonly unrelatedPendingCount: number;
}

export interface CueCopy {
  readonly title: string;
  readonly body: string;
}

export interface CueEvent {
  readonly id: CueEventId;
  readonly kind: CueKind;
  readonly segmentIndex: number;
  readonly segmentRole: CueSegmentRole;
  /** Delay from the projection origin, never an absolute instant. */
  readonly fireAfter: Milliseconds;
  readonly copy: CueCopy;
}

/**
 * Ids must be a pure function of the timeline so a re-projection after a pause
 * produces the same identities — otherwise cancel-and-reschedule would leave
 * duplicates and collision resolution would be non-deterministic.
 */
export function composeCueEventId(
  kind: CueKind,
  segmentIndex: number,
  fireAfter: Milliseconds,
): CueEventId {
  return cueEventId(`${kind}.${segmentIndex}.${fireAfter}`);
}

/**
 * Winner-first ordering for two cues landing on the same instant: kind
 * priority, then the later segment, then id for a total order.
 */
export function compareCueEvents(a: CueEvent, b: CueEvent): number {
  const priority = CUE_KIND_PRIORITY[b.kind] - CUE_KIND_PRIORITY[a.kind];
  if (priority !== 0) return priority;
  const segment = b.segmentIndex - a.segmentIndex;
  if (segment !== 0) return segment;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/** One notification per millisecond instant; the rest are discarded. */
export function resolveCueCollisions(
  events: readonly CueEvent[],
): readonly CueEvent[] {
  const winners = new Map<number, CueEvent>();
  for (const event of events) {
    const existing = winners.get(event.fireAfter);
    if (existing === undefined || compareCueEvents(event, existing) < 0) {
      winners.set(event.fireAfter, event);
    }
  }
  return [...winners.values()].sort((a, b) => a.fireAfter - b.fireAfter);
}

/**
 * Retention tiers applied when the schedule exceeds capacity. Completion is
 * kept first because it is the only cue that tells the user the work is over.
 */
export type CueRetentionTier =
  | "completion"
  | "segmentTransition"
  | "workMilestone"
  | "cadence";

export const CUE_RETENTION_TIERS: readonly CueRetentionTier[] = [
  "completion",
  "segmentTransition",
  "workMilestone",
  "cadence",
];

export function cueRetentionTier(event: CueEvent): CueRetentionTier {
  if (event.kind === "workoutComplete") return "completion";
  if (event.kind === "segmentTransition") return "segmentTransition";
  if (
    event.segmentRole === "work" &&
    (event.kind === "halfway" || event.kind === "finalCountdown")
  ) {
    return "workMilestone";
  }
  return "cadence";
}

/**
 * Evenly spaced retention rather than head-truncation: dropping the tail would
 * leave the last half of a long session entirely silent, which is the part a
 * tiring user most needs cued.
 */
export function sampleEvenly<T>(
  items: readonly T[],
  limit: number,
): readonly T[] {
  if (limit <= 0) return [];
  if (limit >= items.length) return items;
  const sampled: T[] = [];
  for (let i = 0; i < limit; i += 1) {
    const index = Math.floor((i * items.length) / limit);
    const item = items[index];
    if (item !== undefined) sampled.push(item);
  }
  return sampled;
}

export function applyCueScheduleCap(
  events: readonly CueEvent[],
  capacity: number,
): readonly CueEvent[] {
  if (events.length <= capacity) return events;
  const kept: CueEvent[] = [];
  for (const tier of CUE_RETENTION_TIERS) {
    const remaining = capacity - kept.length;
    if (remaining <= 0) break;
    const tierEvents = events.filter((event) => cueRetentionTier(event) === tier);
    kept.push(...sampleEvenly(tierEvents, remaining));
  }
  return kept.sort((a, b) => a.fireAfter - b.fireAfter);
}

/**
 * Which cue kinds a segment may ever produce. Recovery never gets drill
 * switches even when a switch cadence is configured, so rest stays rest.
 */
export function permittedCueKinds(
  segment: CueScheduleSegment,
  policy: CueSchedulePolicy,
): readonly CueKind[] {
  const kinds: CueKind[] = [
    "segmentTransition",
    "halfway",
    "finalCountdown",
    "workoutComplete",
  ];
  if (segment.guidance === "standaloneDrill") return kinds;
  if (segment.role !== "recovery") kinds.push("drillSwitch");
  if (policy.includesBurstCues && segment.burst !== null) {
    kinds.push("burstWork", "burstReset");
  }
  return kinds;
}

// ---------------------------------------------------------------------------
// Cue copy — generic by contract
// ---------------------------------------------------------------------------

/**
 * Every string below is deliberately generic. A scheduled cue is delivered
 * outside the app's privacy surface, so it may contain no plan name, segment
 * title, segment detail, or cooldown copy.
 */
export const CUE_COPY_HALFWAY: CueCopy = {
  title: "Halfway",
  body: "Stay loose and keep your guard ready.",
};

export const CUE_COPY_DRILL_SWITCH_IN_ROUND: CueCopy = {
  title: "New combination",
  body: "Reset your feet and build the next combination.",
};

export const CUE_COPY_DRILL_SWITCH: CueCopy = {
  title: "Switch drill",
  body: "Move cleanly into the next drill.",
};

export const CUE_COPY_BURST_RESET: CueCopy = {
  title: "Moving reset",
  body: "Breathe, move, and keep your hands up.",
};

export const CUE_COPY_BURST_WORK: CueCopy = {
  title: "Work",
  body: "Back on the bag.",
};

export const CUE_COPY_WORKOUT_COMPLETE: CueCopy = {
  title: "Workout complete",
  body: "Your session is in the books.",
};

export const CUE_COUNTDOWN_BODY = "Finish this interval clean." as const;
export const CUE_TRANSITION_BODY_PREFIX = "Round" as const;

export function cueCountdownCopy(seconds: number): CueCopy {
  return { title: `${seconds} seconds`, body: CUE_COUNTDOWN_BODY };
}

/**
 * The transition title is the raw enum value of the next segment kind — a
 * build-time constant, never user text — and the body names only the round
 * ordinal.
 */
export function cueTransitionCopy(
  nextSegmentKindRawValue: string,
  roundNumber: number,
): CueCopy {
  return {
    title: nextSegmentKindRawValue,
    body: `${CUE_TRANSITION_BODY_PREFIX} ${roundNumber}. Next interval.`,
  };
}

export function cueDrillSwitchCopy(role: CueSegmentRole): CueCopy {
  return role === "work" ? CUE_COPY_DRILL_SWITCH_IN_ROUND : CUE_COPY_DRILL_SWITCH;
}

const FIXED_CUE_COPY: readonly CueCopy[] = [
  CUE_COPY_HALFWAY,
  CUE_COPY_DRILL_SWITCH_IN_ROUND,
  CUE_COPY_DRILL_SWITCH,
  CUE_COPY_BURST_RESET,
  CUE_COPY_BURST_WORK,
  CUE_COPY_WORKOUT_COMPLETE,
];

const COUNTDOWN_TITLE = /^\d+ seconds$/;
const TRANSITION_BODY = /^Round \d+\. Next interval\.$/;

/**
 * The privacy rule is enforced by test rather than by convention, and this is
 * the predicate the test asserts on: a projected cue must match the fixed
 * catalog or one of the two parameterized shapes, and nothing else.
 */
export function isGenericCueCopy(copy: CueCopy): boolean {
  for (const fixed of FIXED_CUE_COPY) {
    if (fixed.title === copy.title && fixed.body === copy.body) return true;
  }
  if (COUNTDOWN_TITLE.test(copy.title) && copy.body === CUE_COUNTDOWN_BODY) {
    return true;
  }
  return TRANSITION_BODY.test(copy.body);
}

// ---------------------------------------------------------------------------
// Notification authorization
// ---------------------------------------------------------------------------

/**
 * The native six-state taxonomy. It is retained in full because the
 * schedulable/not-schedulable split — and specifically the product decision
 * that provisional and ephemeral ARE schedulable — is the rule the engine
 * tests assert on, and collapsing it at the domain boundary would lose it.
 */
export type NotificationAuthorization =
  | "notDetermined"
  | "denied"
  | "authorized"
  | "provisional"
  | "ephemeral"
  | "unknown";

export const NOTIFICATION_AUTHORIZATION_STATUS_WORD: Readonly<
  Record<NotificationAuthorization, string>
> = {
  authorized: "Allowed",
  provisional: "Quietly allowed",
  ephemeral: "Temporarily allowed",
  denied: "Off",
  notDetermined: "Optional",
  unknown: "Checking",
};

export function isSchedulableAuthorization(
  status: NotificationAuthorization,
): boolean {
  return (
    status === "authorized" ||
    status === "provisional" ||
    status === "ephemeral"
  );
}

export const NOTIFICATION_SCHEDULE_ERROR_COPY = {
  notDetermined: "Notification permission has not been requested.",
  denied: "Notifications are disabled for Fighting Form in Settings.",
  other: "Fighting Form cannot currently schedule workout notifications.",
} as const;

export function notificationScheduleErrorCopy(
  status: NotificationAuthorization,
): string {
  if (status === "notDetermined") {
    return NOTIFICATION_SCHEDULE_ERROR_COPY.notDetermined;
  }
  if (status === "denied") return NOTIFICATION_SCHEDULE_ERROR_COPY.denied;
  return NOTIFICATION_SCHEDULE_ERROR_COPY.other;
}

/**
 * What the browser actually reports. `unsupported` is a real fourth state: a
 * browser without the Notifications API is not the same as a user who declined.
 */
export type WebNotificationPermission =
  | "unsupported"
  | "default"
  | "granted"
  | "denied";

export function authorizationFromWebPermission(
  permission: WebNotificationPermission,
): NotificationAuthorization {
  switch (permission) {
    case "granted":
      return "authorized";
    case "denied":
      return "denied";
    case "default":
      return "notDetermined";
    case "unsupported":
      return "unknown";
  }
}

/**
 * v1 does not present a lock-screen cue toggle. There is no reliable scheduled
 * notification trigger on the web — a service worker plus `setTimeout` dies
 * with the tab, and iOS Safari requires an installed PWA and supports no custom
 * sound. Shipping a control that cannot deliver is worse than shipping none.
 * The projection above is still built: it drives the foreground timeline.
 */
export const LOCK_SCREEN_CUES_AVAILABLE = false as const;

export const SCREEN_LOCKED_PANEL_COPY = {
  title: "While the tab is hidden",
  body: `Fighting Form cannot deliver scheduled cues from a background browser tab, so it does not offer them. Keep ${DEVICE_NOUN} awake and Fighting Form visible while you train; the round clock, bells, and voice all continue in the foreground.`,
  wakeLockAvailable:
    "Fighting Form will keep the screen awake for the whole workout.",
  wakeLockUnavailable:
    "This browser will not hold the screen awake. Set the screen timeout longer before a long session.",
} as const;

// ---------------------------------------------------------------------------
// Settings navigation and copy
// ---------------------------------------------------------------------------

export type SettingsGroup =
  | "app"
  | "workoutExperience"
  | "reference"
  | "yourData";

export const SETTINGS_GROUP_ORDER: readonly SettingsGroup[] = [
  "app",
  "workoutExperience",
  "reference",
  "yourData",
];

export const SETTINGS_GROUP_TITLE: Readonly<Record<SettingsGroup, string>> = {
  app: "App",
  workoutExperience: "Workout experience",
  reference: "Reference",
  yourData: "Your data",
};

export type SettingsPane =
  | "appearance"
  | "cues"
  | "trainingGuide"
  | "drills"
  | "history"
  | "journalPrivacy"
  | "aboutPrivacy";

export interface SettingsPaneDescriptor {
  readonly pane: SettingsPane;
  readonly group: SettingsGroup;
  readonly title: string;
  readonly detail: string;
  /** Some panes read differently in the compact list; every pane carries both
   *  strings so the compact list never has to decide which one it has. */
  readonly compactDetail: string;
  readonly symbol: string;
}

export const SETTINGS_PANE_DESCRIPTORS: readonly SettingsPaneDescriptor[] = [
  {
    pane: "appearance",
    group: "app",
    title: "Appearance",
    detail: "Follow System, or choose Day or Night",
    compactDetail: "Follow System, or choose Day or Night",
    symbol: "circle.lefthalf.filled",
  },
  {
    pane: "cues",
    group: "workoutExperience",
    title: "Cues & audio",
    // Rewritten rather than translated (§5). The native line named haptics and
    // lock-screen cues; the web has no haptics API and no reliable scheduled-
    // notification trigger, and the pane this row opens already renders both as
    // unavailable. A summary line that contradicts the pane it opens is exactly
    // the claim §2.6 exists to prevent.
    detail: "Bells, spoken calls, and milestone timing",
    compactDetail: "Bells, spoken calls, and milestone timing",
    symbol: "waveform",
  },
  {
    pane: "trainingGuide",
    group: "workoutExperience",
    title: "Training & combinations",
    detail: "Intervals, combinations, and live calls",
    compactDetail: "Intervals, saved combos, and live calls",
    symbol: "list.number",
  },
  {
    pane: "drills",
    group: "reference",
    title: "Drill library",
    detail: "Boxing, defense, rope, strength, recovery",
    compactDetail: "Boxing, defense, rope, strength, recovery",
    symbol: "figure.martial.arts",
  },
  {
    pane: "history",
    group: "yourData",
    title: "Training history",
    detail: "Review, repeat, or remove saved efforts",
    compactDetail: "Review, repeat, or remove saved efforts",
    symbol: "clock.arrow.circlepath",
  },
  {
    pane: "journalPrivacy",
    group: "yourData",
    title: "Journal privacy",
    // "File protection" is iOS `FileProtectionType.complete`, which has no
    // browser analogue — naming it here would promise encryption at rest the
    // web build does not provide. Both lines say only what is true.
    detail: "Concealment and optional Journal Lock",
    compactDetail: "Concealment and optional Journal Lock",
    symbol: "lock.shield",
  },
  {
    pane: "aboutPrivacy",
    group: "yourData",
    title: "About & privacy",
    detail: "Storage, permissions, safety, and version",
    compactDetail: "Local storage, permissions, safety, version",
    symbol: "hand.raised",
  },
];

/** The wide workspace opens on Cues because it is the only pane with state. */
export const DEFAULT_SETTINGS_PANE: SettingsPane = "cues";

export const SETTINGS_HOME_COPY = {
  eyebrow: "Preferences & reference",
  title: "Set up Fighting Form",
  body: "Keep the main screens focused. Detailed controls and reference material live here.",
} as const;

export const SETTINGS_WORKSPACE_COPY = {
  eyebrow: "EQUIPMENT ROOM",
  title: "Set up your corner",
  subtitle: "Controls and reference stay visible while you work.",
} as const;

/**
 * Identifiers are computed from enum raw values and are treated as public API:
 * a rename silently breaks test addressing rather than failing a build.
 */
export const SETTINGS_TEST_IDS = {
  home: "settings.screen",
  workspace: "ipad.settings.workspace",
  appearanceScreen: "appearance.screen",
  appearanceMode: "appearance.mode",
  appearanceStatus: "appearance.status",
  trainingGuideScreen: "settings.trainingGuide.screen",
  cuesScreen: "cues.screen",
  cueStatus: "brand.cueStatus",
  cueTest: "cues.test",
  cueAboutPrivacy: "cues.aboutPrivacy",
  aboutPrivacyScreen: "aboutPrivacy.screen",
  aboutPrivacyVersion: "aboutPrivacy.version",
} as const;

export function settingsPaneTestId(pane: SettingsPane): string {
  return `settings.${pane}`;
}

export function settingsWorkspacePaneTestId(pane: SettingsPane): string {
  return `ipad.settings.${pane}`;
}

/** Layout numbers the compositions branch on; named so they stay in sync. */
export const SETTINGS_METRICS = {
  workspaceMinimumWidth: 680,
  sidebarWidth: 318,
  paneRowMinHeight: 68,
  paneRowAccessibilityMinHeight: 112,
  workspaceButtonMinHeight: 58,
  toggleRowMinHeight: 64,
  toggleRowAccessibilityMinHeight: 104,
  pickerRowMinHeight: 64,
  pickerRowAccessibilityMinHeight: 96,
  linkRowMinHeight: 58,
  previewMomentMinHeight: 58,
  appearancePreviewMinHeight: 154,
  aboutContentMaxWidth: 760,
} as const;

/** "Workouts and recorded effort" until there is something to review. */
export function historyPaneDetail(savedSessionCount: number): string {
  if (savedSessionCount <= 0) return "Workouts and recorded effort";
  const noun = savedSessionCount === 1 ? "session" : "sessions";
  return `${savedSessionCount} saved ${noun} · Review or delete`;
}

// ---------------------------------------------------------------------------
// Cues screen copy
// ---------------------------------------------------------------------------

export const CUES_SCREEN_COPY = {
  navigationTitle: "Cues",
  eyebrow: "Eyes-up cues",
  title: "Hear every change",
  body: "Bells, spoken calls, and haptics can work together while other audio keeps playing.",
  accessibilityNote:
    "Every critical cue is also visible onscreen. With haptics enabled, no workout state depends on sound alone.",
  aboutRowTitle: "About & privacy",
  aboutRowDetail: "Local data, permissions, safety, and app version",
} as const;

export type CuePreviewTone = "signal" | "violet" | "volt" | "sky";

export interface CuePreviewMoment {
  readonly label: string;
  readonly value: string;
  readonly tone: CuePreviewTone;
}

export const CUE_PREVIEW_MOMENTS: readonly CuePreviewMoment[] = [
  { label: "Start", value: "Bell", tone: "signal" },
  { label: "Halfway", value: "1:30", tone: "violet" },
  { label: "Warning", value: "0:10", tone: "volt" },
  { label: "End", value: "Double", tone: "sky" },
];

/**
 * The native title interpolates the device noun ("iPhone audio"). With the
 * neutral noun that reads as "this device audio", so the string is rephrased
 * rather than interpolated.
 */
export const CUE_PREVIEW_COPY = {
  title: "Audio on this device",
  /**
   * Ducking has no web analogue: `.duckOthers` and `.mixWithOthers` do not
   * exist. The honest subtitle is the non-ducking one, always.
   */
  subtitle: "Cues mix at the current music level",
  readyStatus: "Ready",
  visualOnlyStatus: CUE_PILL_VISUAL_ONLY,
} as const;

export interface CueToggleDescriptor {
  readonly key: CueToggleKey;
  readonly title: string;
  readonly detail: string;
  readonly defaultValue: boolean;
  /**
   * False where the platform cannot honor the control. A toggle the user can
   * switch on that changes nothing is a lie the redundancy contract cannot
   * afford.
   */
  readonly availableOnWeb: boolean;
}

export const CUE_OUTPUT_SECTION_TITLE = "Output" as const;

export const CUE_OUTPUT_TOGGLES: readonly CueToggleDescriptor[] = [
  {
    key: "cues.bells",
    title: "Boxing bells",
    detail: "Round starts and transitions",
    defaultValue: true,
    availableOnWeb: true,
  },
  {
    key: "cues.voice",
    title: "Voice guidance",
    detail: "Exercises, rounds, and combinations",
    defaultValue: true,
    availableOnWeb: true,
  },
  {
    key: "cues.haptics",
    title: "Haptics",
    detail: "Feel each important change",
    defaultValue: true,
    // `navigator.vibrate` is absent in Safari and on iOS entirely.
    availableOnWeb: false,
  },
  {
    key: "cues.duckMusic",
    title: "Lower music for voice",
    detail: "Briefly duck other audio",
    defaultValue: true,
    // No web API ducks another tab's or app's audio.
    availableOnWeb: false,
  },
];

export const CUE_MILESTONE_SECTION = {
  title: "Milestones",
  trailing: "Round-level",
} as const;

export const CUE_MILESTONE_TOGGLES: readonly CueToggleDescriptor[] = [
  {
    key: "cues.halfway",
    title: "Halfway",
    detail: "Once per round",
    defaultValue: true,
    availableOnWeb: true,
  },
  {
    key: "cues.finalTen",
    title: "Final ten seconds",
    detail: "Voice and haptic warning",
    defaultValue: true,
    availableOnWeb: true,
  },
  {
    key: "cues.drillChange",
    title: "Drill changes",
    detail: "Announce the next exercise",
    defaultValue: true,
    availableOnWeb: true,
  },
  {
    key: "cues.comboCalls",
    title: "Combo callouts",
    detail: "Read generated sequences",
    defaultValue: true,
    availableOnWeb: true,
  },
];

export const VOICE_STYLE_ROW_COPY = {
  title: "Voice style",
  detail: "Pace and tone of spoken cues",
} as const;

/**
 * The row disappears while voice is off rather than rendering disabled: a
 * disabled picker invites the user to fight a control that has no effect.
 */
export function showsVoiceStyleRow(settings: CueSettings): boolean {
  return settings.voice;
}

export const CUE_TEST_COPY = {
  title: "Test my cues",
  secondary: "Bell + voice",
  speech: "Halfway. Stay loose.",
} as const;

/**
 * The test speaks after the bell rather than over it, so the user hears each
 * channel separately and can tell which one is missing.
 */
export const CUE_TEST_SPEECH_DELAY: Milliseconds = 650;

// ---------------------------------------------------------------------------
// Training guide copy
// ---------------------------------------------------------------------------

export interface TrainingGuideSection {
  readonly ordinal: string;
  readonly title: string;
  readonly body: string;
}

export const TRAINING_GUIDE_COPY = {
  navigationTitle: "Training guide",
  eyebrow: "Workout structure",
  title: "How workouts work",
  intro:
    "Fighting Form keeps workout structure, interval timing, and combination calls separate so each can be changed without surprising the others.",
  footerLabel: "Combo Lab lives in Build",
} as const;

export const TRAINING_GUIDE_SECTIONS: readonly TrainingGuideSection[] = [
  {
    ordinal: "01",
    title: "Sections organize",
    body: "A workout is a sequence of named sections. Add any drill or interval to a section, then reorder the sequence to match how you want to train.",
  },
  {
    ordinal: "02",
    title: "Intervals run the clock",
    body: "Work is the active interval. Rest is the recovery after it. Preparation happens once before the first interval. Exercise changes occur inside longer work without changing its total time.",
  },
  {
    ordinal: "03",
    title: "Combos supply the call",
    body: "Generated creates fresh sequences from your complexity choices. Saved uses only favorites. Mix alternates favorites with generated calls. Skipping asks for a different call without skipping the interval.",
  },
];

export function trainingGuideFooter(savedComboCount: number): CueCopy {
  if (savedComboCount <= 0) {
    return {
      title: "No saved combos yet",
      body: "Save a combo in the generator, then select Saved or Mix when you build a guided boxing section.",
    };
  }
  const noun = savedComboCount === 1 ? "combo" : "combos";
  return {
    title: `${savedComboCount} saved ${noun} ready`,
    body: "Use Saved or Mix as the combo source for a guided boxing section in Build.",
  };
}

// ---------------------------------------------------------------------------
// About & privacy copy
// ---------------------------------------------------------------------------

export type AboutPrivacySection =
  | "privacyAtAGlance"
  | "whatFightingFormStores"
  | "journalPrivacy"
  | "audioMicrophoneNotifications"
  | "yourControls"
  | "moreInformation"
  | "trainingSafety";

/** "More information" is omitted when both external links fail validation. */
export const ABOUT_PRIVACY_SECTION_ORDER: readonly AboutPrivacySection[] = [
  "privacyAtAGlance",
  "whatFightingFormStores",
  "journalPrivacy",
  "audioMicrophoneNotifications",
  "yourControls",
  "moreInformation",
  "trainingSafety",
];

export const ABOUT_PRIVACY_COPY = {
  navigationTitle: "About & privacy",
  eyebrow: "Built for your corner",
  title: "Private by default",
  body: "Fighting Form is a focused training tool with no account, advertising, or behavioral tracking.",
} as const;

export interface PrivacyClaim {
  readonly title: string;
  readonly body: string;
}

export const PRIVACY_AT_A_GLANCE = {
  title: "Privacy at a glance",
  trailing: "On-device",
} as const;

export const PRIVACY_AT_A_GLANCE_CLAIMS: readonly PrivacyClaim[] = [
  {
    title: "No account",
    body: "No sign-in, profile, or cloud identity is required.",
  },
  {
    title: "No tracking",
    body: "No advertising identifiers, third-party analytics, or cross-app tracking.",
  },
  {
    title: "Local training and journal data",
    body: `Your settings, workouts, combinations, journal entries, and selected journal media remain in Fighting Form on ${DEVICE_NOUN}.`,
  },
];

/**
 * The native section claims `FileProtectionType.complete` — data unreadable
 * while the device is locked. No browser storage behaves that way, so the
 * claim is rewritten rather than translated. Overstating protection here is a
 * privacy misrepresentation, not a copy nit.
 */
export const JOURNAL_PRIVACY_SECTION = {
  title: "Journal privacy",
  trailing: "On this device",
} as const;

export const JOURNAL_PRIVACY_CLAIMS: readonly PrivacyClaim[] = [
  {
    title: "Stored in this browser profile",
    body: "Journal entries and media are stored unencrypted in this browser's storage for this site. Anyone who can use this browser profile can read them.",
  },
  {
    title: "Journal Lock",
    body: "Journal Lock adds a device verification step before entries are revealed. Fighting Form never receives biometric data. This is a local privacy gate, not encryption at rest.",
  },
  {
    title: "Never in analytics or notifications",
    body: "Journal prose is not placed in analytics, notifications, or crash reports.",
  },
];

export interface UserControlRow {
  readonly ordinal: string;
  readonly title: string;
  readonly body: string;
}

export const YOUR_CONTROLS_TITLE = "Your controls" as const;

export const YOUR_CONTROLS_ROWS: readonly UserControlRow[] = [
  {
    ordinal: "01",
    title: "History",
    body: "Edit or clear a corner note, delete one session and its corner note, or clear training history. Separately saved Journal recaps remain until you delete them in Journal.",
  },
  {
    ordinal: "02",
    title: "Saved workouts",
    body: "Delete a custom workout from Build → Workouts → My workouts.",
  },
  {
    ordinal: "03",
    title: "Favorites",
    body: "Remove a saved combination by tapping its favorite control again.",
  },
  {
    ordinal: "04",
    title: "Journal",
    body: "Delete individual entries and their media from Journal. Working drafts stay recoverable until you finish or discard them.",
  },
  {
    // Deleting an app has no web equivalent; clearing site data is the real
    // mechanism and names a different action in a different place.
    ordinal: "05",
    title: "Everything",
    body: "Clearing this site's data in your browser settings removes everything Fighting Form has stored on this device.",
  },
];

export const TRAINING_SAFETY_TITLE = "Training safety" as const;

export const TRAINING_SAFETY_COPY: readonly string[] = [
  "Fighting Form provides timers and general exercise prompts. It is not medical advice, a diagnosis, supervision, or a substitute for qualified professional guidance.",
  "Train in a clear space with suitable equipment and technique. Stop immediately if you feel pain, dizziness, unusual shortness of breath, numbness, or loss of control. If you have questions about whether exercise is appropriate for you, consult a qualified healthcare professional before starting.",
];

export const HOME_SAFETY_LINE =
  "Train in clear space. Use wraps and gloves for bag work, and stop for pain, dizziness, or unusual symptoms." as const;

// ---------------------------------------------------------------------------
// External links
// ---------------------------------------------------------------------------

export type ExternalLinkKind = "privacyPolicy" | "support";

export interface ExternalLinkDescriptor {
  readonly kind: ExternalLinkKind;
  readonly title: string;
  readonly symbol: string;
}

export const EXTERNAL_LINK_DESCRIPTORS: readonly ExternalLinkDescriptor[] = [
  { kind: "privacyPolicy", title: "Full privacy policy", symbol: "hand.raised" },
  { kind: "support", title: "Support", symbol: "questionmark.circle" },
];

export const EXTERNAL_LINK_SECTION_TITLE = "More information" as const;
export const EXTERNAL_LINK_HINT = "Opens outside Fighting Form" as const;

export type ExternalLinkRejection =
  | "empty"
  | "whitespace"
  | "unparseable"
  | "scheme"
  | "credentials"
  | "port"
  | "host"
  | "topLevelDomain"
  | "placeholderDomain";

export type ExternalLinkValidation =
  | { readonly valid: true; readonly url: string; readonly host: string }
  | { readonly valid: false; readonly reason: ExternalLinkRejection };

const PLACEHOLDER_TLDS: readonly string[] = [
  "localhost",
  "local",
  "example",
  "invalid",
  "test",
];

const PLACEHOLDER_HOSTS: readonly string[] = [
  "example.com",
  "example.net",
  "example.org",
];

const HOST_LABEL = /^[a-z0-9-]+$/;
const CONTAINS_LETTER = /[a-z]/;

/**
 * Strict by design. These rows open a destination outside the app, so an
 * accidental placeholder or an unencrypted scheme is not rendered at all
 * rather than shown and left for the user to notice. Both links currently
 * ship empty, which is why the fallback path is the live one.
 *
 * Parsed by hand rather than with `URL` so the domain layer stays free of
 * platform globals and the rejection reason stays specific.
 */
export function validateExternalLink(raw: string | null): ExternalLinkValidation {
  if (raw === null) return { valid: false, reason: "empty" };
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { valid: false, reason: "empty" };
  if (/\s/.test(trimmed)) return { valid: false, reason: "whitespace" };

  const prefix = "https://";
  if (trimmed.length <= prefix.length) {
    return { valid: false, reason: "unparseable" };
  }
  if (trimmed.slice(0, prefix.length).toLowerCase() !== prefix) {
    return { valid: false, reason: "scheme" };
  }

  const remainder = trimmed.slice(prefix.length);
  const boundary = remainder.search(/[/?#]/);
  const authority = boundary === -1 ? remainder : remainder.slice(0, boundary);
  if (authority.length === 0) return { valid: false, reason: "host" };
  if (authority.includes("@")) return { valid: false, reason: "credentials" };
  if (authority.includes(":")) return { valid: false, reason: "port" };

  const host = authority.toLowerCase();
  if (host.length > 253) return { valid: false, reason: "host" };

  const labels = host.split(".");
  if (labels.length < 2) return { valid: false, reason: "host" };
  for (const label of labels) {
    if (label.length === 0 || label.length > 63) {
      return { valid: false, reason: "host" };
    }
    if (label.startsWith("-") || label.endsWith("-")) {
      return { valid: false, reason: "host" };
    }
    if (!HOST_LABEL.test(label)) return { valid: false, reason: "host" };
  }

  const tld = labels[labels.length - 1] ?? "";
  if (tld.length < 2 || !CONTAINS_LETTER.test(tld)) {
    return { valid: false, reason: "topLevelDomain" };
  }
  if (PLACEHOLDER_TLDS.includes(tld)) {
    return { valid: false, reason: "topLevelDomain" };
  }
  for (const placeholder of PLACEHOLDER_HOSTS) {
    if (host === placeholder || host.endsWith(`.${placeholder}`)) {
      return { valid: false, reason: "placeholderDomain" };
    }
  }

  return { valid: true, url: trimmed, host };
}

// ---------------------------------------------------------------------------
// Version footer
// ---------------------------------------------------------------------------

export interface AppVersion {
  readonly shortVersion: string | null;
  readonly buildNumber: string | null;
}

export const DEVELOPMENT_BUILD_LABEL = "Development build" as const;

/** Empty and whitespace-only values are absent, not printable. */
export function formatVersionFooter(version: AppVersion): string {
  const short = version.shortVersion?.trim() ?? "";
  const build = version.buildNumber?.trim() ?? "";
  if (short.length > 0 && build.length > 0) {
    return `Version ${short} (${build})`;
  }
  if (short.length > 0) return `Version ${short}`;
  if (build.length > 0) return `Build ${build}`;
  return DEVELOPMENT_BUILD_LABEL;
}

export function versionFooterAccessibilityLabel(version: AppVersion): string {
  return `Fighting Form, ${formatVersionFooter(version)}`;
}

// ---------------------------------------------------------------------------
// The settings aggregate
// ---------------------------------------------------------------------------

export interface FightingFormSettings {
  readonly appearance: AppearanceMode;
  readonly cues: CueSettings;
  readonly accessibility: AccessibilityOverrides;
}

export const DEFAULT_SETTINGS: FightingFormSettings = {
  appearance: DEFAULT_APPEARANCE_MODE,
  cues: DEFAULT_CUE_SETTINGS,
  accessibility: DEFAULT_ACCESSIBILITY_OVERRIDES,
};

export const SETTINGS_SCHEMA_VERSION: SchemaVersion = schemaVersion(1);

// ===========================================================================
// Persistence envelope
// ===========================================================================

/**
 * Every store writes this shape. The version travels with the payload rather
 * than beside it so a byte-for-byte copy of the record is always
 * self-describing — a store cannot be read at the wrong version by reading it
 * from the wrong place.
 */
export interface PersistedEnvelope<T> {
  readonly schemaVersion: SchemaVersion;
  readonly payload: T;
}

export type StoreRecoveryPolicy = "wholeStore" | "perRecordQuarantine";

/**
 * Why a store refused to be read. Each case corresponds to a condition where
 * continuing would mean guessing at the user's data.
 */
export type FailClosedTrigger =
  | "futureSchema"
  | "corruptBytes"
  | "duplicateIdentity"
  | "unsupportedShape"
  /**
   * Manual history rejects any value that would need decode-time
   * normalization — a missing revision, a missing `updatedAt`, an `updatedAt`
   * before `createdAt`, an active duration exceeding wall. Normalizing would
   * silently rewrite what the user recorded.
   */
  | "normalizationRequired";

export interface StoreDescriptor {
  readonly store: StoreId;
  /** null where the store versions its envelope rather than its schema. */
  readonly currentSchemaVersion: SchemaVersion | null;
  readonly failClosedTriggers: readonly FailClosedTrigger[];
  readonly recovery: StoreRecoveryPolicy;
}

export const STORE_IDS = {
  activeStructuredCheckpoint: storeId("activeStructuredCheckpoint"),
  activeOpenEffortCheckpoint: storeId("activeOpenEffortCheckpoint"),
  canonicalEffortSessions: storeId("canonicalEffortSessions"),
  legacyWorkoutRecords: storeId("legacyWorkoutRecords"),
  savedPlans: storeId("savedPlans"),
  /**
   * Saved / favourite combos. This constant is the only authority for what
   * object stores exist, so the store combat.ts's `PersistedComboScore` is
   * written to has to be named here rather than conjured by a
   * `createObjectStore` call the constant does not know about. Combos are
   * authored training material that a guided boxing section can draw from
   * (`trainingGuideFooter`, spec F-CB-39), which is why they get their own
   * store and not a corner of `settings`: a preference blob has no per-record
   * identity to key, index, or report a duplicate on.
   */
  savedCombos: storeId("savedCombos"),
  sessionReflections: storeId("sessionReflections"),
  journalEntries: storeId("journalEntries"),
  journalDrafts: storeId("journalDrafts"),
  journalGoals: storeId("journalGoals"),
  settings: storeId("settings"),
} as const;

export const STORE_DESCRIPTORS: readonly StoreDescriptor[] = [
  {
    store: STORE_IDS.activeStructuredCheckpoint,
    currentSchemaVersion: schemaVersion(2),
    failClosedTriggers: ["futureSchema", "corruptBytes"],
    recovery: "wholeStore",
  },
  {
    store: STORE_IDS.activeOpenEffortCheckpoint,
    currentSchemaVersion: schemaVersion(1),
    failClosedTriggers: ["futureSchema", "corruptBytes"],
    recovery: "wholeStore",
  },
  {
    store: STORE_IDS.canonicalEffortSessions,
    currentSchemaVersion: schemaVersion(2),
    failClosedTriggers: [
      "futureSchema",
      "corruptBytes",
      "duplicateIdentity",
      "unsupportedShape",
      "normalizationRequired",
    ],
    recovery: "wholeStore",
  },
  {
    store: STORE_IDS.legacyWorkoutRecords,
    currentSchemaVersion: schemaVersion(2),
    failClosedTriggers: ["futureSchema", "corruptBytes"],
    recovery: "wholeStore",
  },
  {
    store: STORE_IDS.savedPlans,
    currentSchemaVersion: null,
    failClosedTriggers: ["futureSchema", "duplicateIdentity", "corruptBytes"],
    recovery: "wholeStore",
  },
  {
    /**
     * Mirrors `savedPlans` deliberately. `PersistedComboScore` carries no
     * payload version and treats every field as optional so a stored favourite
     * always decodes, which means the envelope is the only thing that can be
     * versioned — hence `null`. Recovery is whole-store rather than per-record
     * because a saved combo is not just a library entry: Build can select
     * `saved` or `mixed` as a section's combo source, so a silently shortened
     * list would change what a guided round calls without the user ever being
     * told a favourite went missing. `duplicateIdentity` is a trigger for the
     * same reason it is on `savedPlans` — two records under one `ComboId` make
     * "the combo this section referenced" unanswerable.
     */
    store: STORE_IDS.savedCombos,
    currentSchemaVersion: null,
    failClosedTriggers: ["futureSchema", "duplicateIdentity", "corruptBytes"],
    recovery: "wholeStore",
  },
  {
    store: STORE_IDS.sessionReflections,
    currentSchemaVersion: schemaVersion(2),
    failClosedTriggers: ["futureSchema", "corruptBytes"],
    recovery: "wholeStore",
  },
  {
    store: STORE_IDS.journalEntries,
    currentSchemaVersion: schemaVersion(2),
    failClosedTriggers: ["futureSchema", "corruptBytes"],
    recovery: "perRecordQuarantine",
  },
  {
    store: STORE_IDS.journalDrafts,
    currentSchemaVersion: schemaVersion(1),
    failClosedTriggers: ["futureSchema", "corruptBytes"],
    recovery: "perRecordQuarantine",
  },
  {
    store: STORE_IDS.journalGoals,
    currentSchemaVersion: schemaVersion(1),
    failClosedTriggers: ["futureSchema", "corruptBytes"],
    recovery: "perRecordQuarantine",
  },
  {
    store: STORE_IDS.settings,
    currentSchemaVersion: SETTINGS_SCHEMA_VERSION,
    failClosedTriggers: ["futureSchema", "corruptBytes"],
    recovery: "wholeStore",
  },
];

export function storeDescriptor(store: StoreId): StoreDescriptor | undefined {
  for (const descriptor of STORE_DESCRIPTORS) {
    if (descriptor.store === store) return descriptor;
  }
  return undefined;
}

/**
 * One record that could not be decoded but whose bytes are preserved. Only
 * stores with a per-record recovery policy produce these; the rest fail whole.
 */
export interface QuarantinedRecord {
  readonly recordKey: string;
  readonly trigger: FailClosedTrigger;
  readonly detail: string;
  readonly preservedByteLength: number;
}

/**
 * The tri-state that the whole fail-closed contract rests on. `missing` and
 * `unreadable` are separate cases because the app must never silently replace
 * an unreadable store with an empty one — a user whose history cannot be
 * decoded has history, and overwriting it destroys it permanently.
 */
export type StoreReadResult<T> =
  | { readonly status: "missing"; readonly store: StoreId }
  | {
      readonly status: "loaded";
      readonly store: StoreId;
      readonly schemaVersion: SchemaVersion;
      readonly value: T;
      /** Empty unless the store quarantines individual records. */
      readonly quarantined: readonly QuarantinedRecord[];
    }
  | {
      readonly status: "unreadable";
      readonly store: StoreId;
      readonly trigger: FailClosedTrigger;
      readonly detail: string;
      /** Bytes are kept exactly as found so a later version can recover them. */
      readonly preservedByteLength: number;
      readonly observedSchemaVersion: SchemaVersion | null;
    };

/**
 * Mutation is gated rather than attempted-and-rolled-back, so a blocked store
 * never has to be restored from a copy that may itself be untrustworthy.
 */
export type MutationGate =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly reason: FailClosedTrigger };

export function mutationGate<T>(result: StoreReadResult<T>): MutationGate {
  if (result.status === "unreadable") {
    return { allowed: false, reason: result.trigger };
  }
  return { allowed: true };
}

/**
 * Reading a value must go through a case analysis, never through a helper
 * that returns `T | undefined` — that helper is exactly how "unreadable"
 * degrades into "empty".
 */
export function isStoreReadable<T>(result: StoreReadResult<T>): boolean {
  return result.status !== "unreadable";
}

export const FAIL_CLOSED_RECOVERY_COPY: Readonly<
  Record<FailClosedTrigger, string>
> = {
  futureSchema: `This data was saved by a newer version of Fighting Form. Update Fighting Form on ${DEVICE_NOUN} to read it. Nothing has been changed or deleted.`,
  corruptBytes: `Fighting Form could not read this saved data on ${DEVICE_NOUN}. It has been left exactly as found and will not be overwritten.`,
  duplicateIdentity:
    "Fighting Form found two saved records with the same identity and stopped rather than choose between them. Nothing has been changed or deleted.",
  unsupportedShape:
    "This saved data is in a form Fighting Form does not recognize. It has been left exactly as found and will not be overwritten.",
  normalizationRequired:
    "This saved session would have to be altered to be read, so Fighting Form left it untouched rather than change what you recorded.",
};

// ---------------------------------------------------------------------------
// Write verification
// ---------------------------------------------------------------------------

/**
 * Every write is verified by re-reading and comparing canonical encodings.
 * `JSON.stringify` does not sort keys, so the comparison is only meaningful
 * against a hand-written canonical serializer — a mismatch is a failure, never
 * a formatting difference.
 */
export type WriteOutcome =
  | { readonly status: "verified" }
  | {
      readonly status: "mismatch";
      readonly expectedCanonical: string;
      readonly actualCanonical: string | null;
    }
  | {
      readonly status: "refused";
      readonly reason: FailClosedTrigger;
    };

export function verifyWrite(
  expectedCanonical: string,
  reReadCanonical: string | null,
): WriteOutcome {
  if (reReadCanonical === expectedCanonical) return { status: "verified" };
  return {
    status: "mismatch",
    expectedCanonical,
    actualCanonical: reReadCanonical,
  };
}

/**
 * The stated maximum loss window. Snapshot writes coalesce inside it, but
 * semantic transitions bypass coalescing entirely, so no state change that
 * alters what the user performed can ever wait on the timer.
 */
export const SNAPSHOT_COALESCE_WINDOW: Milliseconds = 5_000;

// ---------------------------------------------------------------------------
// Migration
// ---------------------------------------------------------------------------

export type MigrationOutcome<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly trigger: FailClosedTrigger;
      readonly detail: string;
    };

/**
 * A migration step is a pure function from one decoded shape to the next.
 * Steps are idempotent by contract and a legacy source is only removed after
 * the merge succeeds, so an interrupted migration re-runs safely.
 */
export interface MigrationDescriptor<From, To> {
  readonly store: StoreId;
  readonly from: SchemaVersion;
  readonly to: SchemaVersion;
  readonly migrate: (input: From) => MigrationOutcome<To>;
}

export type MigrationPlan =
  | { readonly kind: "upToDate" }
  | {
      readonly kind: "migrate";
      readonly path: readonly SchemaVersion[];
    }
  /** Newer bytes than this build understands. Refuse; never downgrade. */
  | {
      readonly kind: "refuseFutureSchema";
      readonly observed: SchemaVersion;
      readonly current: SchemaVersion;
    }
  /** An older version with no registered step out of it. */
  | {
      readonly kind: "unsupportedPath";
      readonly observed: SchemaVersion;
      readonly current: SchemaVersion;
    };

/**
 * Planning is separated from running so the refusal decision can be made and
 * tested without touching storage, and so a refusal is a value rather than a
 * thrown error that a caller might swallow.
 */
export function planMigration(
  descriptors: readonly MigrationDescriptor<unknown, unknown>[],
  store: StoreId,
  observed: SchemaVersion,
  current: SchemaVersion,
): MigrationPlan {
  if (observed === current) return { kind: "upToDate" };
  if (observed > current) {
    return { kind: "refuseFutureSchema", observed, current };
  }

  const path: SchemaVersion[] = [observed];
  let cursor = observed;
  while (cursor !== current) {
    let next: SchemaVersion | undefined;
    for (const descriptor of descriptors) {
      if (descriptor.store === store && descriptor.from === cursor) {
        next = descriptor.to;
        break;
      }
    }
    if (next === undefined || next <= cursor) {
      return { kind: "unsupportedPath", observed, current };
    }
    path.push(next);
    cursor = next;
  }
  return { kind: "migrate", path };
}
